import * as React from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { SiteShell } from "@/components/SiteShell";
import { useAdminGate } from "@/hooks/useAdminGate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import {
  getSiteHealth,
  listClientErrors,
  ackClientError,
  ackAllClientErrors,
  type ClientErrorRow,
  type HealthCheck,
} from "@/lib/siteHealth.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertCircle, CheckCircle2, AlertTriangle, RefreshCcw, Eye, EyeOff, ExternalLink, CheckCheck } from "lucide-react";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { useAdminTexts } from "@/lib/useAdminTexts";
import { IsolationRuntimePanel } from "@/components/admin/IsolationRuntimePanel";

export const Route = createFileRoute("/admin/site-health")({
  component: SiteHealthPage,
  head: () => ({
    meta: [{ title: "صحّة الموقع | Lecmo Admin" }],
  }),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="p-8 text-center">
        <p className="mb-4 text-destructive">حدث خطأ: {error instanceof Error ? error.message : String(error)}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>إعادة المحاولة</Button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-8 text-center">الصفحة غير موجودة</div>,
});

function statusBadge(status: HealthCheck["status"]) {
  if (status === "ok") return <Badge className="bg-green-600 text-white"><CheckCircle2 className="me-1 h-3 w-3" />سليم</Badge>;
  if (status === "warn") return <Badge className="bg-amber-500 text-white"><AlertTriangle className="me-1 h-3 w-3" />بطيء</Badge>;
  return <Badge className="bg-destructive text-destructive-foreground"><AlertCircle className="me-1 h-3 w-3" />متعطّل</Badge>;
}

function SiteHealthPage() {
  useAdminTexts();
  const { ready } = useAdminGate();
  const fetchHealth = useServerFn(getSiteHealth);
  const fetchErrors = useServerFn(listClientErrors);
  const ackFn = useServerFn(ackClientError);
  const ackAllFn = useServerFn(ackAllClientErrors);
  const [onlyUnacked, setOnlyUnacked] = React.useState(true);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const healthQ = useQuery({
    queryKey: ["site-health"],
    queryFn: () => fetchHealth(),
    enabled: ready,
    refetchInterval: 60_000,
  });

  const errorsQ = useQuery({
    queryKey: ["client-errors", onlyUnacked],
    queryFn: () => fetchErrors({ data: { onlyUnacked, limit: 100 } }),
    enabled: ready,
  });

  const ackMut = useMutation({
    mutationFn: (vars: { id: string; ack: boolean }) => ackFn({ data: vars }),
    onSuccess: () => {
      toast.success("تمّ التحديث");
      void errorsQ.refetch();
      void healthQ.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const ackAllMut = useMutation({
    mutationFn: () => ackAllFn(),
    onSuccess: (res) => {
      toast.success(`تمّت معالجة ${res.count} خطأً`);
      void errorsQ.refetch();
      void healthQ.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">صحّة الموقع</h1>
        {/* الفحوصات الفوريّة */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">حالة الخدمات</h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void healthQ.refetch()}
              disabled={healthQ.isFetching}
            >
              <RefreshCcw className={`me-2 h-3.5 w-3.5 ${healthQ.isFetching ? "animate-spin" : ""}`} />
              تحديث
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {healthQ.data?.checks.map((c) => (
              <Card key={c.name} className="p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-medium">{c.name}</p>
                  {statusBadge(c.status)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {c.latency_ms != null ? `${c.latency_ms} ms · ` : ""}
                  {c.detail}
                </p>
              </Card>
            ))}
            {!healthQ.data && <p className="text-sm text-muted-foreground">جارٍ الفحص…</p>}
          </div>
        </section>

        <IsolationRuntimePanel />

        {/* KPIs */}
        <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">أخطاء آخر ٢٤ ساعة</p>
            <p className="mt-1 text-2xl font-bold">{healthQ.data?.errors_24h ?? "—"}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">أخطاء فريدة</p>
            <p className="mt-1 text-2xl font-bold">{healthQ.data?.errors_24h_unique ?? "—"}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">غير معالجة</p>
            <p className="mt-1 text-2xl font-bold text-destructive">{healthQ.data?.errors_unacked ?? "—"}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">موبايل / ديسكتوب</p>
            <p className="mt-1 text-sm">
              📱 {healthQ.data?.errors_by_device?.mobile ?? 0} · 💻 {healthQ.data?.errors_by_device?.desktop ?? 0}
            </p>
          </Card>
        </section>

        {/* قائمة الأخطاء */}
        <section>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">آخر أخطاء العملاء</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setOnlyUnacked((v) => !v)}>
                {onlyUnacked ? <Eye className="me-2 h-3.5 w-3.5" /> : <EyeOff className="me-2 h-3.5 w-3.5" />}
                {onlyUnacked ? "عرض الكلّ" : "غير المعالَجة فقط"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="default"
                    disabled={ackAllMut.isPending || (healthQ.data?.errors_unacked ?? 0) === 0}
                  >
                    <CheckCheck className="me-2 h-3.5 w-3.5" />
                    معالجة الكلّ ({healthQ.data?.errors_unacked ?? 0})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>معالجة كلّ الأخطاء غير المعالَجة؟</AlertDialogTitle>
                    <AlertDialogDescription>
                      سيتمّ تعليم {healthQ.data?.errors_unacked ?? 0} خطأً كـ«معالَج». البيانات لا تُحذَف — تبقى للمراجعة عبر «عرض الكلّ».
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>إلغاء</AlertDialogCancel>
                    <AlertDialogAction onClick={() => ackAllMut.mutate()}>تأكيد المعالجة</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>


          {errorsQ.isLoading && <p className="text-sm text-muted-foreground">جارٍ التحميل…</p>}
          {errorsQ.data && errorsQ.data.length === 0 && (
            <Card className="p-8 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-green-600" />
              <p className="font-medium">لا أخطاء — الموقع يعمل بسلاسة</p>
            </Card>
          )}

          <div className="space-y-2">
            {errorsQ.data?.map((row) => (
              <ErrorRow
                key={row.id}
                row={row}
                expanded={expandedId === row.id}
                onToggle={() => setExpandedId(expandedId === row.id ? null : row.id)}
                onAck={(ack) => ackMut.mutate({ id: row.id, ack })}
                acking={ackMut.isPending}
              />
            ))}
          </div>
        </section>
      </div>
      </div>
    </SiteShell>
  );
}

function ErrorRow({
  row,
  expanded,
  onToggle,
  onAck,
  acking,
}: {
  row: ClientErrorRow;
  expanded: boolean;
  onToggle: () => void;
  onAck: (ack: boolean) => void;
  acking: boolean;
}) {
  const date = new Date(row.created_at);
  return (
    <Card className={`p-3 ${row.ack ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={onToggle} className="flex-1 text-start">
          <p className="text-sm font-medium line-clamp-2">{row.message}</p>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>{date.toLocaleString("ar")}</span>
            <span>·</span>
            <Badge variant="outline" className="text-[10px]">{row.source}</Badge>
            {row.device_type && <Badge variant="outline" className="text-[10px]">{row.device_type}</Badge>}
            {row.market && <Badge variant="outline" className="text-[10px]">{row.market}</Badge>}
            {row.url && (
              <span className="inline-flex items-center gap-1 truncate max-w-[40ch]">
                <ExternalLink className="h-3 w-3" />
                {new URL(row.url, PUBLIC_SITE_ORIGIN).pathname}
              </span>
            )}
          </div>
        </button>
        <Button
          size="sm"
          variant={row.ack ? "outline" : "default"}
          disabled={acking}
          onClick={() => onAck(!row.ack)}
        >
          {row.ack ? "إلغاء المعالجة" : "تمّت المعالجة"}
        </Button>
      </div>
      {expanded && row.stack && (
        <pre className="mt-3 max-h-64 overflow-auto rounded bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
          {row.stack}
        </pre>
      )}
    </Card>
  );
}
